import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DoorOpen, AlertCircle, Plus, RefreshCw } from 'lucide-react';
import { useFactory } from '../../../context/FactoryContext';
import { useCurrentEmployee } from '../../../hooks/useCurrentEmployee';
import { usePermissions } from '../../../hooks/usePermissions';
import { isLaborRestrictedViewer, isProdSupervisorTeamScoped, canManageFactoryTeams } from '../../../utils/roleChecks';
import { filterTeamsByRole, collectTeamEmployeeIds } from '../../../utils/teamScope';
import { resolveLaborFactoryId, getEmployeeDisplayName } from '../calendario/utils/laborFactoryUtils';
import { employeeService } from '../empleados/services/employeeService';
import { teamService } from '../empleados/services/teamService';
import { LaborAbsenceFormModal } from './components/LaborAbsenceFormModal';
import { LaborAbsenceTable } from './components/LaborAbsenceTable';
import { laborAbsenceService } from './services/laborAbsenceService';
import {
  ABSENCE_STATUS_FILTER_OPTIONS,
  yearDateRange,
} from './utils/laborAbsenceUtils';

const SalidasEspeciales = () => {
  const { selectedFactory, factories } = useFactory();
  const currentYear = new Date().getFullYear();
  const { roleKeys, isLoading: permissionsLoading } = usePermissions();
  const isViewer = useMemo(() => isLaborRestrictedViewer(roleKeys), [roleKeys]);
  const isSupervisorScoped = useMemo(() => isProdSupervisorTeamScoped(roleKeys), [roleKeys]);
  const canManageAbsences = useMemo(() => canManageFactoryTeams(roleKeys), [roleKeys]);

  const [year, setYear] = useState(currentYear);
  const [statusFilter, setStatusFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const { employeeId: currentEmployeeId, employeeName: currentEmployeeName, isReady: hasCurrentEmployee } =
    useCurrentEmployee();
  const [editingAbsence, setEditingAbsence] = useState(null);
  const [actionError, setActionError] = useState(null);

  const factoryId = useMemo(
    () => resolveLaborFactoryId(selectedFactory, factories),
    [selectedFactory, factories]
  );
  const factoryBase = selectedFactory || '';

  const { from, to } = useMemo(() => yearDateRange(year), [year]);

  const { data: supervisorTeams = [], isLoading: loadingSupervisorTeams } = useQuery({
    queryKey: ['labor-teams-absences-scope', factoryId, currentEmployeeId],
    queryFn: () => teamService.getTeams(),
    enabled: Boolean(factoryId) && isSupervisorScoped && hasCurrentEmployee,
    staleTime: 60_000,
    select: (teams) =>
      filterTeamsByRole(teams, {
        factoryId,
        roleKeys,
        leaderEmployeeId: currentEmployeeId,
      }),
  });

  const supervisorTeam = supervisorTeams[0] ?? null;

  const { data: supervisorTeamMembers = [] } = useQuery({
    queryKey: ['labor-team-members-absences', supervisorTeam?.id],
    queryFn: () => teamService.getTeamMembers(supervisorTeam.id),
    enabled: Boolean(supervisorTeam?.id),
  });

  const teamEmployeeIds = useMemo(() => {
    if (!isSupervisorScoped) return null;
    return collectTeamEmployeeIds(supervisorTeam, supervisorTeamMembers);
  }, [isSupervisorScoped, supervisorTeam, supervisorTeamMembers]);

  const effectiveEmployeeFilter = useMemo(() => {
    if (isViewer && currentEmployeeId != null) {
      return String(currentEmployeeId);
    }
    return employeeFilter;
  }, [isViewer, currentEmployeeId, employeeFilter]);

  const { data: employees = [], isLoading: loadingEmployees } = useQuery({
    queryKey: ['labor-absence-employees', factoryId, factoryBase],
    queryFn: () => employeeService.getEmployees(factoryBase, true, factoryId),
    enabled: Boolean(factoryId || factoryBase),
    staleTime: 60_000,
  });

  const {
    data: absences = [],
    isLoading: loadingAbsences,
    isFetching: fetchingAbsences,
    refetch,
    error: queryError,
  } = useQuery({
    queryKey: ['labor-absences', factoryId, year, statusFilter, effectiveEmployeeFilter],
    queryFn: () =>
      laborAbsenceService.listAbsences({
        factoryId,
        employeeId: effectiveEmployeeFilter || null,
        status: statusFilter,
        from,
        to,
      }),
    enabled:
      Boolean(factoryId) &&
      (isViewer
        ? hasCurrentEmployee && Boolean(effectiveEmployeeFilter)
        : isSupervisorScoped
          ? hasCurrentEmployee
          : true),
  });

  const visibleAbsences = useMemo(() => {
    if (!isSupervisorScoped) return absences;
    if (!teamEmployeeIds?.size) return [];
    return absences.filter((a) => teamEmployeeIds.has(Number(a.employee_id)));
  }, [absences, isSupervisorScoped, teamEmployeeIds]);

  useEffect(() => {
    setEmployeeFilter('');
    setActionError(null);
  }, [factoryId]);

  useEffect(() => {
    if (!employeeFilter || !isSupervisorScoped) return;
    if (teamEmployeeIds && !teamEmployeeIds.has(Number(employeeFilter))) {
      setEmployeeFilter('');
    }
  }, [supervisorTeam, teamEmployeeIds, employeeFilter, isSupervisorScoped]);

  const employeeNameById = useMemo(() => {
    const map = {};
    employees.forEach((emp) => {
      map[emp.id] = getEmployeeDisplayName(emp);
    });
    return map;
  }, [employees]);

  const sortedEmployees = useMemo(
    () =>
      [...employees].sort((a, b) =>
        getEmployeeDisplayName(a).localeCompare(getEmployeeDisplayName(b), 'es')
      ),
    [employees]
  );

  const selectableEmployees = useMemo(() => {
    if (!isSupervisorScoped) return sortedEmployees;
    if (!teamEmployeeIds?.size) return [];
    return sortedEmployees.filter((emp) => teamEmployeeIds.has(Number(emp.id)));
  }, [sortedEmployees, isSupervisorScoped, teamEmployeeIds]);

  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear + 1; y >= currentYear - 2; y -= 1) {
      years.push(y);
    }
    return years;
  }, [currentYear]);

  const handleOpenCreate = () => {
    setEditingAbsence(null);
    setFormOpen(true);
  };

  const handleEdit = (absence) => {
    setEditingAbsence(absence);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingAbsence(null);
  };

  const listError = queryError?.message || actionError;

  if (permissionsLoading) return null;

  return (
    <div className="p-4 sm:p-6 w-full min-w-0">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
          <DoorOpen className="w-8 h-8 text-blue-400" />
          Ausencias y permisos
        </h1>
        <p className="text-gray-400">
          {isSupervisorScoped
            ? 'Aprobación de ausencias de tu equipo. La fábrica del header filtra los registros.'
            : isViewer
              ? 'Consulta tus ausencias y permisos por año.'
              : 'Vacaciones, bajas médicas y otros permisos. La fábrica del header filtra empleados y registros.'}
        </p>
      </div>

      {!factoryId && (
        <div className="mb-4 flex items-center gap-2 text-amber-400 bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>Selecciona una fábrica en el header para gestionar ausencias.</span>
        </div>
      )}

      {factoryId && (
        <>
          <div className="mb-4 flex flex-wrap gap-3 items-end bg-gray-800/80 border border-gray-700 rounded-lg p-4">
            <div>
              <label htmlFor="absence-year" className="block text-xs text-gray-400 mb-1">
                Año
              </label>
              <select
                id="absence-year"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm min-w-[100px]"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1 min-w-[160px] max-w-xs">
              <label htmlFor="absence-status" className="block text-xs text-gray-400 mb-1">
                Estado
              </label>
              <select
                id="absence-status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
              >
                {ABSENCE_STATUS_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1 min-w-[200px] max-w-sm">
              <label htmlFor="absence-employee-filter" className="block text-xs text-gray-400 mb-1">
                {isSupervisorScoped ? 'Compañero de equipo' : 'Empleado'}
              </label>
              {isViewer ? (
                <div className="w-full bg-gray-700/50 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm">
                  {currentEmployeeName || '—'}
                </div>
              ) : (
                <select
                  id="absence-employee-filter"
                  value={employeeFilter}
                  onChange={(e) => setEmployeeFilter(e.target.value)}
                  disabled={
                    loadingEmployees ||
                    (isSupervisorScoped && (loadingSupervisorTeams || !supervisorTeam))
                  }
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50"
                >
                  <option value="">
                    {isSupervisorScoped
                      ? loadingSupervisorTeams
                        ? 'Cargando equipo…'
                        : 'Todos del equipo'
                      : loadingEmployees
                        ? 'Cargando…'
                        : 'Todos los empleados'}
                  </option>
                  {selectableEmployees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {getEmployeeDisplayName(emp)}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <button
              type="button"
              onClick={() => refetch()}
              disabled={fetchingAbsences}
              className="px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded-lg border border-gray-600 disabled:opacity-50 inline-flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${fetchingAbsences ? 'animate-spin' : ''}`} />
              Actualizar
            </button>

            {!isViewer && (
              <button
                type="button"
                onClick={handleOpenCreate}
                className="ml-auto px-4 py-2 text-sm bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Nueva ausencia
              </button>
            )}
          </div>

          {isSupervisorScoped && !loadingSupervisorTeams && !supervisorTeam && (
            <div className="mb-4 flex items-center gap-2 text-amber-400 bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span>No tienes ningún equipo asignado como líder.</span>
            </div>
          )}

          {factoryId && isViewer && !hasCurrentEmployee && (
            <div className="mb-4 flex items-center gap-2 text-amber-400 bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span>Tu usuario no está vinculado a un empleado; no puedes consultar tus ausencias.</span>
            </div>
          )}

          {factoryId && !isViewer && !hasCurrentEmployee && (
            <div className="mb-4 flex items-center gap-2 text-amber-400 bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span>Tu usuario no está vinculado a un empleado; no podrás aprobar ausencias.</span>
            </div>
          )}

          {listError && (
            <div className="mb-4 p-3 rounded-lg border border-red-800 bg-red-900/20 flex gap-2 text-red-300 text-sm">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span>{listError}</span>
            </div>
          )}

          <LaborAbsenceTable
            absences={visibleAbsences}
            isLoading={
              loadingAbsences ||
              (isViewer && !hasCurrentEmployee) ||
              (isSupervisorScoped && loadingSupervisorTeams)
            }
            isFetching={fetchingAbsences}
            employeeNameById={employeeNameById}
            approverEmployeeId={currentEmployeeId}
            onEdit={canManageAbsences ? handleEdit : undefined}
            onActionError={(msg) => {
              setActionError(msg);
            }}
            readOnly={isViewer}
            approvalOnly={isSupervisorScoped}
          />
        </>
      )}

      <LaborAbsenceFormModal
        isOpen={formOpen}
        onClose={handleFormClose}
        factoryId={factoryId}
        editingAbsence={editingAbsence}
        defaultEmployeeId={editingAbsence ? null : effectiveEmployeeFilter || null}
        employeeOptions={selectableEmployees}
        onSuccess={() => {
          setActionError(null);
          refetch();
        }}
      />
    </div>
  );
};

export default SalidasEspeciales;
